import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useServicePricingService from '../../app/services/hooks/useServicePricingService';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import InputField from '../../components/InputField';

interface ServicePricing {
    id: number;
    service_type: string;
    service_type_display: string;
    plan_name: string;
    base_price: number;
    base_price_formatted: string;
    currency: string;
    description: string;
    is_default: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

const ServicePricingList: React.FC = () => {
    const history = useHistory();
    const { requestConfirmation } = useReactConfirmAlert();
    const {
        getServicePricings,
        deleteServicePricing,
        activeServicePricing,
        fetchingGetServicePricings,
        fetchingDeleteServicePricing,
        fetchingActiveServicePricing
    } = useServicePricingService();
    
    const [servicePricings, setServicePricings] = useState<ServicePricing[]>([]);
    const [filteredPricings, setFilteredPricings] = useState<ServicePricing[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    useEffect(() => {
        loadServicePricings();
    }, []);
    
    useEffect(() => {
        filterData();
    }, [searchTerm, servicePricings]);
    
    const loadServicePricings = () => {
        getServicePricings({}, {
            onSuccess: (res: any) => {
                setServicePricings(res.data.service_pricings || []);
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar precios de servicios');
            }
        });
    };
    
    const filterData = () => {
        let filtered = [...servicePricings];
        
        // Filtrar por búsqueda
        if (searchTerm) {
            filtered = filtered.filter(pricing =>
                pricing.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pricing.service_type_display?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pricing.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        setFilteredPricings(filtered);
    };
    
    const handleEdit = (id: number) => {
        history.push(`/intranet/service-pricing/${id}/edit`);
    };

    const handleCreate = () => {
        history.push('/intranet/service-pricing/create');
    };

    const handleDelete = (pricing: ServicePricing) => {
        requestConfirmation({
            title: '¿Eliminar plan?',
            message: `¿Estás seguro de eliminar el plan "${pricing.plan_name}"? Esta acción no se puede deshacer.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteServicePricing(pricing.id, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || 'Plan eliminado correctamente');
                                loadServicePricings();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || 'Error al eliminar el plan');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const handleToggleActive = (pricing: ServicePricing) => {
        const newStatus = !pricing.is_active;
        const actionText = newStatus ? 'activar' : 'desactivar';
        
        requestConfirmation({
            title: `¿${newStatus ? 'Activar' : 'Desactivar'} plan?`,
            message: `¿Estás seguro de ${actionText} el plan "${pricing.plan_name}"?`,
            buttons: {
                confirmButton: {
                    label: `Sí, ${actionText}`,
                    onClick: () => {
                        activeServicePricing(pricing.id, newStatus, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || `Plan ${newStatus ? 'activado' : 'desactivado'} correctamente`);
                                loadServicePricings();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || `Error al ${actionText} el plan`);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };
    
    // Mostrar loading mientras se cargan los datos
    if (fetchingGetServicePricings) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Planes de Servicios', isCurrent: true }
                ]}
                title="Planes de Servicios"
                description="Gestión de planes y precios para los servicios ofrecidos"
            />
            
            <DefaultCard>
                {/* Filtros y Búsqueda */}
                <div className="row mb-3 align-items-end">
                    <div className="col-md-6">
                        <InputField
                            label="Buscar"
                            value={searchTerm}
                            onChange={(value) => setSearchTerm(value)}
                            placeholder="Buscar por nombre de plan, servicio o descripción..."
                            icon="fa-search"
                        />
                    </div>
                    <div className="col-md-6 d-flex justify-content-end">
                        <ButtonGeneral
                            onClick={handleCreate}
                            title="Nuevo Plan"
                            variant="primary"
                            size="small"
                            icon="fa-plus"
                            iconPosition="left"
                        />
                    </div>
                </div>
                
                {/* Tabla */}
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Tipo de Servicio</th>
                                <th>Nombre del Plan</th>
                                <th>Precio Base</th>
                                <th>Estado</th>
                                <th>Descripción</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPricings.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-4">
                                        {searchTerm
                                            ? 'No se encontraron planes con los filtros aplicados'
                                            : 'No hay planes de servicios registrados'}
                                    </td>
                                </tr>
                            ) : (
                                filteredPricings.map((pricing) => (
                                    <tr 
                                        key={pricing.id}
                                        style={{ opacity: pricing.is_active ? 1 : 0.6 }}
                                    >
                                        <td>
                                            <span className="badge bg-secondary">
                                                {pricing.service_type_display || pricing.service_type}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="fw-bold" style={{ color: '#001849' }}>
                                                {pricing.plan_name}
                                            </span>
                                            {pricing.is_default && (
                                                <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem' }}>
                                                    Por defecto
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="fw-bold">
                                                {pricing.base_price_formatted}
                                            </span>
                                            <span className="text-muted ms-1" style={{ fontSize: '0.85rem' }}>
                                                {pricing.currency}
                                            </span>
                                        </td>
                                        <td>
                                            <span 
                                                className={`badge ${pricing.is_active ? 'bg-success' : 'bg-secondary'}`}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                {pricing.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                                {pricing.description
                                                    ? pricing.description.length > 30
                                                        ? `${pricing.description.substring(0, 30)}...`
                                                        : pricing.description
                                                    : '-'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <ButtonTableAction
                                                    callbackFunction={() => handleEdit(pricing.id)}
                                                    title="Editar"
                                                    classIcon="fa-edit"
                                                    colorIcon="text-warning"
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleToggleActive(pricing)}
                                                    title={pricing.is_active ? 'Desactivar' : 'Activar'}
                                                    classIcon={pricing.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}
                                                    colorIcon={pricing.is_active ? 'text-success' : 'text-secondary'}
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleDelete(pricing)}
                                                    title="Eliminar"
                                                    classIcon="fa-trash"
                                                    colorIcon="text-danger"
                                                    sizeIcon="16"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Información */}
                <div className="mt-3">
                    <small className="text-muted">
                        Mostrando {filteredPricings.length} de {servicePricings.length} plan(es)
                    </small>
                </div>
            </DefaultCard>
        </div>
    );
};

export default ServicePricingList;
